import React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { useNavigate } from 'react-router-dom';
import SLUGS from 'resources/slugs';
import { IconLogout, Iconproducts } from 'assets/icons';
import { convertSlugToUrl } from 'resources/utilities';
import LogoComponent from './LogoComponent';
import Menu from './MenuComponent';
import MenuItem from './MenuItemComponent';
import { useDispatch, useSelector } from 'react-redux';
import { logout, selectUser } from 'features/userSlice';
import {
    CardMembership,
    ContactMailRounded,
    ContactMailSharp,
    Payment,
    Message
} from '@mui/icons-material';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import InsightsIcon from '@mui/icons-material/Insights';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import HomeIcon from '@mui/icons-material/Home';

const useStyles = createUseStyles({
    separator: {
        borderTop: ({ theme }) => `1px solid ${theme.color.lightGreenIshVibe}`,
        marginTop: 16,
        marginBottom: 16,
        opacity: 0.06
    },
    logoContainer: {
        position: 'relative',
        paddingTop: 20,
        paddingBottom: 20,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%'
    },
    toggleButton: ({ collapsed }) => ({
        position: 'absolute',
        top: 15,
        left: collapsed ? 18 : 18,
        zIndex: 1000,
        border: 'none',
        background: 'transparent',
        borderRadius: '8px',
        padding: '6px 8px',
        cursor: 'pointer',
        transition: 'all 0.3s ease-out',
        transform: collapsed ? 'rotate(0deg)' : 'rotate(180deg)',
        color: '#919191ff',
        fontSize: '24px',
        '&:hover': {
            color: '#ffffffff',
            transform: collapsed ? 'rotate(0deg) scale(1.05)' : 'rotate(180deg) scale(1.1)'
        }
    })
});

function SidebarComponent({ collapsed = false, onToggle }) {
    const navigate = useNavigate();
    const user = useSelector(selectUser);
    const theme = useTheme();
    const classes = useStyles({ theme, collapsed });
    const isMobile = window.innerWidth <= 1080;

    // async function logout() {
    //     // log out
    //     push(SLUGS.login);
    // }

    function onClick(slug, parameters = {}) {
        navigate(convertSlugToUrl(slug, parameters));
    }
    const dispatch = useDispatch();

    async function handleLogout() {
        // Clear localStorage
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.clear(); // Clear all localStorage as fallback

        // Clear Redux state
        dispatch(logout());

        // Redirect to login (optional, but ensures clean logout)
        window.location.href = '/login';
    }

    return (
        <Menu isMobile={isMobile} collapsed={collapsed}>
            <div className={classes.logoContainer}>
                <LogoComponent collapsed={collapsed} />
                {onToggle && (
                    <button
                        className={classes.toggleButton}
                        onClick={onToggle}
                        title={collapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
                    >
                        ←
                    </button>
                )}
            </div>

            {(user.role === 'admin' || user.role === 'agent') && (
                <MenuItem
                    id={SLUGS.dashboard}
                    title={collapsed ? '' : 'Dashboard'}
                    icon={HomeIcon}
                    collapsed={collapsed}
                    onClick={() => onClick(SLUGS.dashboard)}
                />
            )}

            {user.role === 'admin' && (
                <MenuItem
                    id={SLUGS.agents}
                    title={collapsed ? '' : 'Agents'}
                    icon={SupportAgentIcon}
                    collapsed={collapsed}
                    onClick={() => onClick(SLUGS.agents)}
                />
            )}
            {user.role === 'admin' && (
                <MenuItem
                    id={SLUGS.credit}
                    title={collapsed ? '' : 'Credit'}
                    icon={LocalAtmIcon}
                    collapsed={collapsed}
                    onClick={() => onClick(SLUGS.credit)}
                />
            )}
            {user.role === 'admin' && (
                <MenuItem
                    id={SLUGS.analytics}
                    title={collapsed ? '' : 'Analytics'}
                    icon={InsightsIcon}
                    collapsed={collapsed}
                    onClick={() => onClick(SLUGS.analytics)}
                />
            )}

            {user.role === 'admin' && (
                <MenuItem
                    id={SLUGS.products}
                    title={collapsed ? '' : 'Products'}
                    icon={Iconproducts}
                    collapsed={collapsed}
                    onClick={() => onClick(SLUGS.products)}
                />
            )}

            {user.role === 'admin' && (
                <MenuItem
                    id={SLUGS.packages}
                    title={collapsed ? '' : 'Packages'}
                    icon={ContactMailSharp}
                    collapsed={collapsed}
                    onClick={() => onClick(SLUGS.packages)}
                />
            )}
            {/* 
            {
                user.role === 'admin' && (<MenuItem
                    id={SLUGS.payoutCash}
                    title='Payments'
                    icon={Payment}
                    onClick={() => onClick(SLUGS.payoutCash)}
                />)
            } */}

            {user.role === 'admin' && (
                <MenuItem
                    id={SLUGS.transactions}
                    title={collapsed ? '' : 'Transactions'}
                    icon={Payment}
                    collapsed={collapsed}
                    onClick={() => onClick(SLUGS.transactions)}
                />
            )}

            {user.role === 'admin' && (
                <MenuItem
                    id={SLUGS.individualSavingStatement}
                    title={collapsed ? '' : 'Statements'}
                    icon={Payment}
                    collapsed={collapsed}
                    onClick={() => onClick(SLUGS.individualSavingStatement)}
                />
            )}

            {user.role === 'admin' && (
                <MenuItem
                    id={SLUGS.bulkSms}
                    title={collapsed ? '' : 'Send Messages'}
                    icon={Message}
                    collapsed={collapsed}
                    onClick={() => onClick(SLUGS.bulkSms)}
                />
            )}

            {user.role === 'admin' && !collapsed && (
                <MenuItem
                    id={SLUGS.scratchCards}
                    items={[SLUGS.scratchCardsTwo, SLUGS.scratchCardsTwo]}
                    title='Scratch Cards'
                    icon={CardMembership}
                >
                    <MenuItem
                        id={SLUGS.scratchCards}
                        title='Cards products'
                        level={2}
                        icon={CardMembership}
                        onClick={() => onClick(SLUGS.scratchCards)}
                    />
                    <MenuItem
                        id={SLUGS.scratchCardsTwo}
                        title='Create New Cards'
                        level={2}
                        icon={ContactMailRounded}
                        onClick={() => onClick(SLUGS.scratchCardsTwo)}
                    />
                </MenuItem>
            )}

            {user.role === 'admin' && collapsed && (
                <MenuItem
                    id={SLUGS.scratchCards}
                    title=''
                    icon={CardMembership}
                    collapsed={collapsed}
                    onClick={() => onClick(SLUGS.scratchCards)}
                />
            )}

            {/* {(user.role === 'admin' || user.role === 'agent') && (
                <MenuItem
                    id={SLUGS.contacts}
                    title={collapsed ? '' : 'Support Contacts'}
                    icon={IconContacts}
                    collapsed={collapsed}
                    onClick={() => onClick(SLUGS.contacts)}
                />
            )} */}

            <div className={classes.separator}></div>
            <MenuItem
                id='logout'
                title={collapsed ? '' : 'Logout'}
                icon={IconLogout}
                collapsed={collapsed}
                onClick={() => {
                    handleLogout();
                }}
            />
        </Menu>
    );
}

export default SidebarComponent;
